import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { io, Socket } from 'socket.io-client';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBlcOaGsV6wV6u-bd1onOicoZPBUbX8RE8';
const API_BASE = 'https://weridesudan.com/api/admin';
const SOCKET_URL = 'https://weridesudan.com';
const DEFAULT_CENTER = { lat: 15.5007, lng: 32.5599 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const POLL_INTERVAL = 5000;
const PULSE_TTL = 10000;

interface Driver {
  _id: string;
  name: string;
  phone: string;
  isAvailable: boolean;
  location: { coordinates: [number, number] };
}

interface Ride {
  _id: string;
  status: string;
  pickup: { coordinates?: [number, number]; address?: string };
  dropoff: { coordinates?: [number, number]; address?: string };
  rider?: { name: string };
  driver?: { name: string };
}

type ConnStatus = 'connecting' | 'live' | 'polling' | 'error';

const makeDriverIcon = (isAvailable: boolean, pulse: boolean) => {
  const color = isAvailable ? '#22c55e' : '#f97316';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
    ${pulse ? `<circle cx="18" cy="18" r="16" fill="${color}" opacity="0.2">
      <animate attributeName="r" values="9;17;9" dur="1.4s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.2;0;0.2" dur="1.4s" repeatCount="indefinite"/>
    </circle>` : ''}
    <circle cx="18" cy="18" r="9" fill="${color}" stroke="white" stroke-width="2.5"/>
    <circle cx="18" cy="18" r="4" fill="white"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(36, 36),
    anchor: new window.google.maps.Point(18, 18),
  };
};

const LiveMap: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [rides, setRides] = useState<Ride[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [lastUpdated, setLastUpdated] = useState('');
  const [error, setError] = useState('');
  const [connStatus, setConnStatus] = useState<ConnStatus>('connecting');
  const [recentlyMoved, setRecentlyMoved] = useState<Set<string>>(new Set());
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [adminLocation, setAdminLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      pos => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setAdminLocation(loc);
        // Only pan the map here if fitBounds hasn't run yet (no drivers online)
        if (prevDriverCountRef.current === 0) {
          setCenter(loc);
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  }, []);

  const mapRef = useRef<google.maps.Map | null>(null);
  const prevDriverCountRef = useRef(0);
  const socketRef = useRef<Socket | null>(null);
  const prevPositionsRef = useRef<Map<string, [number, number]>>(new Map());
  const pulseTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  const markMoved = useCallback((driverId: string) => {
    setRecentlyMoved(prev => { const s = new Set(prev); s.add(driverId); return s; });
    const existing = pulseTimersRef.current.get(driverId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      setRecentlyMoved(prev => { const s = new Set(prev); s.delete(driverId); return s; });
      pulseTimersRef.current.delete(driverId);
    }, PULSE_TTL);
    pulseTimersRef.current.set(driverId, t);
  }, []);

  const applyDriverUpdate = useCallback((newDrivers: Driver[]) => {
    newDrivers.forEach(d => {
      if (!d.location?.coordinates) return;
      const prev = prevPositionsRef.current.get(d._id);
      const [lng, lat] = d.location.coordinates;
      if (!prev || prev[0] !== lng || prev[1] !== lat) {
        markMoved(d._id);
        prevPositionsRef.current.set(d._id, [lng, lat]);
      }
    });
    setDrivers(newDrivers);
  }, [markMoved]);

  const fitDriverBounds = useCallback((driverList: Driver[]) => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const valid = driverList.filter(d => d.location?.coordinates?.length === 2);
    if (valid.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    valid.forEach(d => {
      bounds.extend({ lat: d.location.coordinates[1], lng: d.location.coordinates[0] });
    });
    map.fitBounds(bounds, 80);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE}/map-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      applyDriverUpdate(data.drivers || []);
      setRides(data.activeRides || []);
      setLastUpdated(new Date().toLocaleTimeString());
      setError('');
    } catch {
      setError('Could not load map data');
      setConnStatus('error');
    }
  }, [applyDriverUpdate]);

  // Fit bounds when drivers first appear or a new driver comes online
  useEffect(() => {
    const validCount = drivers.filter(d => d.location?.coordinates?.length === 2).length;
    if (validCount > 0 && (prevDriverCountRef.current === 0 || validCount > prevDriverCountRef.current)) {
      fitDriverBounds(drivers);
    }
    if (validCount > 0) prevDriverCountRef.current = validCount;
  }, [drivers, fitDriverBounds]);

  // Polling fallback — always runs, Socket.io updates land on top
  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  // Socket.io real-time layer
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      timeout: 8000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnStatus('live');
      setError('');
    });
    socket.on('disconnect', () => setConnStatus('polling'));
    socket.on('connect_error', () => setConnStatus('polling'));

    // Backend emits per-driver position update
    socket.on('driverLocationUpdate', ({ driverId, lat, lng }: { driverId: string; lat: number; lng: number }) => {
      markMoved(driverId);
      prevPositionsRef.current.set(driverId, [lng, lat]);
      setDrivers(prev => prev.map(d =>
        d._id === driverId
          ? { ...d, location: { coordinates: [lng, lat] } }
          : d
      ));
      setLastUpdated(new Date().toLocaleTimeString());
    });

    // Backend emits full map snapshot
    socket.on('mapUpdate', (data: { drivers: Driver[]; activeRides: Ride[] }) => {
      applyDriverUpdate(data.drivers || []);
      setRides(data.activeRides || []);
      setLastUpdated(new Date().toLocaleTimeString());
    });

    return () => {
      socket.disconnect();
      pulseTimersRef.current.forEach(t => clearTimeout(t));
    };
  }, [markMoved, applyDriverUpdate]);

  const ll = (coords?: [number, number]) => {
    if (!coords || coords.length < 2) return null;
    return { lat: coords[1], lng: coords[0] };
  };

  const validDrivers = drivers.filter(d => d.location?.coordinates?.length === 2);
  const validRides = rides.filter(r => r.pickup?.coordinates?.length === 2 && r.dropoff?.coordinates?.length === 2);

  const statusDot: Record<ConnStatus, string> = {
    connecting: '#9ca3af',
    live: '#22c55e',
    polling: '#f59e0b',
    error: '#ef4444',
  };
  const statusLabel: Record<ConnStatus, string> = {
    connecting: 'Connecting…',
    live: 'Live',
    polling: `Polling 5s`,
    error: 'Error',
  };

  return (
    <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 20 }}>
          <span style={{ fontSize: 14 }}>
            <strong style={{ color: '#22c55e' }}>{validDrivers.length}</strong> Online Drivers
          </span>
          <span style={{ fontSize: 14 }}>
            <strong style={{ color: '#f97316' }}>{validRides.length}</strong> Active Rides
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusDot[connStatus], display: 'inline-block' }} />
            {statusLabel[connStatus]}
          </span>
          <span style={{ fontSize: 12, color: error ? '#ef4444' : '#9ca3af' }}>
            {error || (lastUpdated && `Updated ${lastUpdated}`)}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#9ca3af' }}>
        {([['#22c55e', 'Available'], ['#f97316', 'On a ride'], ['#3b82f6', 'Pickup'], ['#ef4444', 'Dropoff']] as [string, string][]).map(([c, l]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' }} />
            {l}
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', outline: '2px solid #22c55e', outlineOffset: 2, display: 'inline-block' }} />
          Recently moved
        </span>
      </div>

      {/* Map */}
      <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden' }}>
        {!isLoaded ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2937', color: '#9ca3af' }}>
            Loading map…
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={center}
            zoom={12}
            onLoad={map => { mapRef.current = map; }}
            options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false }}
          >
            {adminLocation && (
              <Marker
                position={adminLocation}
                title="Your location"
                zIndex={999}
                icon={{
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
                      <circle cx="14" cy="14" r="13" fill="#4285F4" opacity="0.15"/>
                      <circle cx="14" cy="14" r="8" fill="#4285F4" stroke="white" stroke-width="2.5"/>
                      <circle cx="14" cy="14" r="3.5" fill="white"/>
                    </svg>`
                  )}`,
                  scaledSize: new window.google.maps.Size(28, 28),
                  anchor: new window.google.maps.Point(14, 14),
                }}
              />
            )}

            {validDrivers.map(d => {
              const pos = ll(d.location.coordinates);
              if (!pos) return null;
              const pulse = recentlyMoved.has(d._id);
              return (
                <Marker
                  key={d._id}
                  position={pos}
                  icon={makeDriverIcon(d.isAvailable, pulse)}
                  onClick={() => { setSelectedDriver(d); setSelectedRide(null); }}
                />
              );
            })}

            {validRides.map(r => {
              const pickup = ll(r.pickup.coordinates);
              const dropoff = ll(r.dropoff.coordinates);
              if (!pickup || !dropoff) return null;
              return (
                <React.Fragment key={r._id}>
                  <Marker
                    position={pickup}
                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
                    onClick={() => { setSelectedRide(r); setSelectedDriver(null); }}
                  />
                  <Marker
                    position={dropoff}
                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
                    onClick={() => { setSelectedRide(r); setSelectedDriver(null); }}
                  />
                  <Polyline
                    path={[pickup, dropoff]}
                    options={{ strokeColor: '#6366f1', strokeOpacity: 0.6, strokeWeight: 2 }}
                  />
                </React.Fragment>
              );
            })}

            {selectedDriver && ll(selectedDriver.location.coordinates) && (
              <InfoWindow position={ll(selectedDriver.location.coordinates)!} onCloseClick={() => setSelectedDriver(null)}>
                <div style={{ fontSize: 13, lineHeight: '1.6' }}>
                  <strong>{selectedDriver.name}</strong><br />
                  {selectedDriver.phone}<br />
                  <span style={{ color: selectedDriver.isAvailable ? '#16a34a' : '#ea580c' }}>
                    {selectedDriver.isAvailable ? 'Available' : 'On a ride'}
                  </span>
                  {recentlyMoved.has(selectedDriver._id) && (
                    <><br /><span style={{ fontSize: 11, color: '#6366f1' }}>Just moved</span></>
                  )}
                </div>
              </InfoWindow>
            )}

            {selectedRide && ll(selectedRide.pickup.coordinates) && (
              <InfoWindow position={ll(selectedRide.pickup.coordinates)!} onCloseClick={() => setSelectedRide(null)}>
                <div style={{ fontSize: 13, lineHeight: '1.6' }}>
                  <strong>Ride — {selectedRide.status.replace('_', ' ')}</strong><br />
                  {selectedRide.rider && <>{selectedRide.rider.name}<br /></>}
                  {selectedRide.driver && <>{selectedRide.driver.name}<br /></>}
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    {selectedRide.pickup.address || 'Pickup'} → {selectedRide.dropoff.address || 'Dropoff'}
                  </span>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>
    </div>
  );
};

export default LiveMap;
